import {useCallback, useEffect, useRef, useState} from "react"
import axios from "axios"
import {Alert, Badge, Button, Card, Col, Row, Spinner, Table} from "react-bootstrap"
import moment from "moment"

import {useAuth} from "../../context/AuthProvider"
import {useTranslation} from "react-i18next"

type MailJob = {
    id: string
    to: string
    prefix: string
    template: string
    scheduledFor: string
    attempts: number
    lastError?: string
    failedAt?: string
    createdAt: string
    updatedAt: string
}

type SentMail = MailJob & {sentAt: string}
type QueueResponse = {
    maxAttempts: number
    pending: MailJob[]
    failed: MailJob[]
    serverTime: string
}

const POLL_INTERVAL_MS = 2000

export default function MailQueueComponent() {
    const {t} = useTranslation("admin")
    const auth = useAuth()
    const isAdmin = auth.hasGate("admin")
    const [pending, setPending] = useState<MailJob[]>([])
    const [failed, setFailed] = useState<MailJob[]>([])
    const [sent, setSent] = useState<SentMail[]>([])
    const [maxAttempts, setMaxAttempts] = useState(5)
    const [lastUpdated, setLastUpdated] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [actionMessage, setActionMessage] = useState<string | null>(null)
    const [expediting, setExpediting] = useState(false)
    const previousPending = useRef<Map<string, MailJob>>(new Map())
    const initialized = useRef(false)
    const requestRunning = useRef(false)

    const refresh = useCallback(async () => {
        if (!isAdmin || requestRunning.current) return
        requestRunning.current = true
        try {
            const response = await axios.get<QueueResponse>(
                "/api/mail-queue",
                auth.axiosAuthHeader()
            )
            const currentIds = new Set([
                ...response.data.pending.map(job => job.id),
                ...response.data.failed.map(job => job.id),
            ])

            if (initialized.current) {
                const newlySent: SentMail[] = []
                previousPending.current.forEach(job => {
                    if (!currentIds.has(job.id)) {
                        newlySent.push({...job, sentAt: response.data.serverTime})
                    }
                })
                if (newlySent.length) {
                    setSent(previous => [...newlySent, ...previous].slice(0, 200))
                }
            }

            previousPending.current = new Map(
                response.data.pending.map(job => [job.id, job])
            )
            initialized.current = true
            setPending(response.data.pending)
            setFailed(response.data.failed)
            setMaxAttempts(response.data.maxAttempts)
            setLastUpdated(response.data.serverTime)
            setError(null)
        } catch {
            setError(t("mailQueue.messages.loadError"))
        } finally {
            requestRunning.current = false
            setLoading(false)
        }
    }, [auth, isAdmin, t])

    useEffect(() => {
        void refresh()
        const interval = window.setInterval(() => void refresh(), POLL_INTERVAL_MS)
        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") void refresh()
        }
        document.addEventListener("visibilitychange", onVisibilityChange)
        return () => {
            window.clearInterval(interval)
            document.removeEventListener("visibilitychange", onVisibilityChange)
        }
    }, [refresh])

    const futurePendingCount = pending.filter(job =>
        moment(job.scheduledFor).isAfter(moment())
    ).length

    const sendFutureNow = async () => {
        if (!futurePendingCount || !window.confirm(t(
            "mailQueue.confirmSendFutureNow",
            {count: futurePendingCount}
        ))) return

        setExpediting(true)
        setActionMessage(null)
        setError(null)
        try {
            const response = await axios.post<{queued: number}>(
                "/api/mail-queue/send-future-now",
                {},
                auth.axiosAuthHeader()
            )
            setActionMessage(t("mailQueue.messages.sendFutureNowSuccess", {
                count: response.data.queued,
            }))
            await refresh()
        } catch {
            setError(t("mailQueue.messages.sendFutureNowError"))
        } finally {
            setExpediting(false)
        }
    }

    const jobTable = (jobs: MailJob[], mode: "pending" | "failed") => (
        <Table responsive hover size="sm" className="align-middle mb-0">
            <thead><tr>
                <th>{t("mailQueue.columns.recipient")}</th>
                <th>{t("mailQueue.columns.template")}</th>
                <th>{mode === "failed"
                    ? t("mailQueue.columns.failedAt")
                    : t("mailQueue.columns.scheduledFor")}</th>
                <th>{t("mailQueue.columns.attempts")}</th>
                {mode === "failed" && <th>{t("mailQueue.columns.error")}</th>}
            </tr></thead>
            <tbody>{jobs.map(job => <tr key={job.id}>
                <td>{job.to}</td>
                <td><code>{job.template}</code></td>
                <td>{moment(mode === "failed"
                    ? job.failedAt ?? job.updatedAt
                    : job.scheduledFor).format("DD.MM.YYYY HH:mm:ss")}</td>
                <td><Badge bg={job.attempts > 0 ? "warning" : "secondary"}>
                    {job.attempts}/{maxAttempts}
                </Badge></td>
                {mode === "failed" && <td className="text-danger">{job.lastError ?? "–"}</td>}
            </tr>)}</tbody>
        </Table>
    )

    if (!isAdmin) return <div className="component"><Alert variant="danger">
        {t("adminOnly")}
    </Alert></div>

    return <div className="component">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
            <h2 className="mb-0"><i className="bi bi-inboxes me-2"/>{t("mailQueue.title")}</h2>
            <div className="d-flex flex-wrap align-items-center gap-2">
                <Button
                    size="sm"
                    variant="warning"
                    disabled={loading || expediting || futurePendingCount === 0}
                    onClick={() => void sendFutureNow()}
                >
                    {expediting
                        ? <Spinner animation="border" size="sm" className="me-2"/>
                        : <i className="bi bi-send-fill me-2"/>}
                    {t("mailQueue.sendFutureNow", {count: futurePendingCount})}
                </Button>
                <div className="text-muted small">
                    <i className="bi bi-arrow-repeat me-1"/>
                    {lastUpdated
                        ? t("mailQueue.lastUpdated", {time: moment(lastUpdated).format("HH:mm:ss")})
                        : t("mailQueue.updating")}
                </div>
            </div>
        </div>
        {error && <Alert variant="warning">{error}</Alert>}
        {actionMessage && <Alert variant="success">{actionMessage}</Alert>}
        {loading ? <div className="text-center"><Spinner/></div> : <div className="d-grid gap-3">
            <Card border={failed.length ? "danger" : undefined}>
                <Card.Header className="d-flex justify-content-between">
                    <strong><i className="bi bi-exclamation-octagon me-2"/>{t("mailQueue.failed")}</strong>
                    <Badge bg="danger">{failed.length}</Badge>
                </Card.Header>
                <Card.Body className="p-0">
                    {failed.length ? jobTable(failed, "failed")
                        : <p className="text-muted text-center p-3 mb-0">{t("mailQueue.emptyFailed")}</p>}
                </Card.Body>
            </Card>

            <Row className="g-3">
                <Col xs={12} lg={6}>
                    <Card className="h-100">
                        <Card.Header className="d-flex justify-content-between">
                            <strong><i className="bi bi-hourglass-split me-2"/>{t("mailQueue.pending")}</strong>
                            <Badge bg="primary">{pending.length}</Badge>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {pending.length ? jobTable(pending, "pending")
                                : <p className="text-muted text-center p-3 mb-0">{t("mailQueue.emptyPending")}</p>}
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} lg={6}>
                    <Card className="h-100">
                        <Card.Header className="d-flex justify-content-between">
                            <strong><i className="bi bi-check-circle me-2"/>{t("mailQueue.sent")}</strong>
                            <Badge bg="success">{sent.length}</Badge>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {sent.length ? <Table responsive hover size="sm" className="align-middle mb-0">
                                <thead><tr>
                                    <th>{t("mailQueue.columns.recipient")}</th>
                                    <th>{t("mailQueue.columns.template")}</th>
                                    <th>{t("mailQueue.columns.sentAt")}</th>
                                </tr></thead>
                                <tbody>{sent.map(job => <tr key={job.id}>
                                    <td>{job.to}</td>
                                    <td><code>{job.template}</code></td>
                                    <td>{moment(job.sentAt).format("DD.MM.YYYY HH:mm:ss")}</td>
                                </tr>)}</tbody>
                            </Table> : <p className="text-muted text-center p-3 mb-0">{t("mailQueue.emptySent")}</p>}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>}
    </div>
}
